import { useEffect, useState } from 'react'

import { SbModal, SbButton, SbSelect, SyncOutlined, WarningFilled } from 'uiCore'
import { useHasAccess } from 'Routes/hooks/useHasAccess'
import {
  TransferModalTitleRow,
  TransferModalTitleIcon,
  TransferDescription,
  TransferWarningBox,
  TransferWarningText,
  TransferFieldLabel,
  TransferHelperText,
} from 'Pages/PeoplePlusAIV2/dashboard/ManageGroupsModal.styles'
import {
  SettingsModalFooterStyled,
  ConflictProjectListStyled,
  ConflictProjectItemStyled,
  ConflictProjectNameStyled,
  ConflictOwnerSelectWrapperStyled,
  ConflictItemMetaStyled,
} from './EmployeeFunctionAccess.styles'
import { PAIC_OWNER_PROFILES } from './Helper'

const DEFAULT_CONFLICT_MESSAGE =
  'The new scope would leave this user unable to see JDs on PAIC projects they own. Transfer ownership of these projects first.'

const OWNER_PROFILES_LABEL = PAIC_OWNER_PROFILES.join(' or ')

const pluralize = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

// Jobs Library JDs can't be moved from here (Ownership design, part 8: an HR Admin transfers ownership, ends the
// delegation or share, or reassigns the approver first)
export const JD_CONFLICT_HELP =
  'These Jobs Library JDs must be transferred, have their delegation or share ended, or have their approver reassigned before this change can be saved.'

const OwnershipConflictModal = ({
  open,
  message,
  projects = [],
  jds = [],
  ownerOptions = [],
  isTransferring = false,
  transferStatusText,
  onTransfer,
  onCancel,
}) => {
  const { hasAccess } = useHasAccess()
  const canTransfer = hasAccess('people_plus_ai', ['edit'])

  const [newOwnerEmail, setNewOwnerEmail] = useState()

  useEffect(() => {
    if (!open) setNewOwnerEmail(undefined)
  }, [open])

  const isTransferDisabled = !canTransfer || !newOwnerEmail || !projects.length || isTransferring

  const handleTransfer = () => {
    if (!isTransferDisabled) onTransfer(newOwnerEmail)
  }

  const footer = (
    <SettingsModalFooterStyled>
      <SbButton buttonType="secondary" onClick={onCancel} disabled={isTransferring}>Cancel</SbButton>
      {/* PAIC projects only: a JD-only conflict has nothing to transfer from here */}
      {projects.length > 0 && (
        <SbButton buttonType="primary" onClick={handleTransfer} disabled={isTransferDisabled} loading={isTransferring}>
          Transfer &amp; Save
        </SbButton>
      )}
    </SettingsModalFooterStyled>
  )

  return (
    <SbModal
      open={open}
      title={
        <TransferModalTitleRow>
          <TransferModalTitleIcon><SyncOutlined /></TransferModalTitleIcon>
          Ownership Conflict
        </TransferModalTitleRow>
      }
      onCancel={onCancel}
      closable={!isTransferring}
      keyboard={!isTransferring}
      maskClosable={false}
      footer={footer}
      width={560}
      centered
      destroyOnClose
    >
      <TransferDescription>{message || DEFAULT_CONFLICT_MESSAGE}</TransferDescription>

      {jds.length > 0 && (
        <>
          <TransferFieldLabel>Blocking Jobs Library JDs ({jds.length})</TransferFieldLabel>
          <ConflictProjectListStyled data-testid="blocking-jds">
            {jds.map((jd) => (
              <ConflictProjectItemStyled key={jd.job_id}>
                <ConflictProjectNameStyled>{jd.label}</ConflictProjectNameStyled>
                {jd.roles && <ConflictItemMetaStyled>{jd.roles}</ConflictItemMetaStyled>}
              </ConflictProjectItemStyled>
            ))}
          </ConflictProjectListStyled>
          <TransferHelperText>{JD_CONFLICT_HELP}</TransferHelperText>
        </>
      )}

      {(projects.length > 0 || !jds.length) && (
        <>
          <TransferFieldLabel>Blocking PAIC projects ({projects.length})</TransferFieldLabel>
          <ConflictProjectListStyled>
            {projects.map((project) => (
              <ConflictProjectItemStyled key={project.sb_project_unique_id}>
                <ConflictProjectNameStyled>{project.project_name}</ConflictProjectNameStyled>
              </ConflictProjectItemStyled>
            ))}
          </ConflictProjectListStyled>

          <TransferWarningBox>
            <WarningFilled />
            <TransferWarningText>
              {canTransfer
                ? `All ${pluralize(projects.length, 'project')} above will be transferred to the selected owner, then this access change is saved automatically.`
                : 'You need PAIC edit access to transfer these projects. Ask a PAIC administrator to transfer them, then save this change again.'}
            </TransferWarningText>
          </TransferWarningBox>

          <TransferFieldLabel>New owner</TransferFieldLabel>
          <ConflictOwnerSelectWrapperStyled>
            <SbSelect
              placeholder="Select new owner"
              options={ownerOptions}
              value={newOwnerEmail}
              onChange={(value) => setNewOwnerEmail(value || undefined)}
              showSearch
              optionFilterProp="label"
              allowClear
              disabled={!canTransfer || isTransferring}
              // Only explain the profile rule when nobody qualifies, not when a search just has no match
              notFoundContent={ownerOptions.length ? undefined : `No ${OWNER_PROFILES_LABEL} users available`}
            />
          </ConflictOwnerSelectWrapperStyled>
          <TransferHelperText aria-live="polite">
            {isTransferring && transferStatusText
              ? transferStatusText
              : `Only ${OWNER_PROFILES_LABEL} profiles are listed. The user being edited is excluded.`}
          </TransferHelperText>
        </>
      )}
    </SbModal>
  )
}

export default OwnershipConflictModal
